/**
 * B2B error copy (UX-9).
 *
 * THE PROBLEM THIS SOLVES. `errorHandler.js` returns `err.message` verbatim in
 * every environment, and the shared axios interceptor toasts whatever comes
 * back. So an unhandled pg error hands the operator its own SQL -
 * `insert into "b2b_invoices" … - duplicate key value violates unique
 * constraint "b2b_invoices_cycle_unique"` - and a request that fails without a
 * body produces `Request failed (409).` Neither tells a person at a counter
 * what to do, and both erode trust in the screen.
 *
 * THE RULE. Nothing technical reaches the operator: no status code, no
 * "undefined", no stack frame, no constraint name, no SQL. Every B2B failure
 * resolves to a sentence that names what happened and what to do about it.
 *
 * TWO LAYERS, in order:
 *   1. a KNOWN CODE maps to copy written for it - the server's `code` is a
 *      contract, its `message` is not;
 *   2. otherwise the server's message is ALLOWED THROUGH ONLY IF it reads like
 *      prose. The B2B controllers deliberately write operator-facing sentences
 *      ("Room A104 belongs to a different client."), and discarding those in
 *      favour of a generic line would lose real information. Anything that
 *      smells technical is dropped for the status-based fallback instead.
 *
 * Deliberately NOT a blanket "something went wrong": that is the other failure
 * mode, where every error looks identical and nobody can act on any of them.
 */

/** What the caller was doing, so a fallback can still say something useful. */
import { SERVER_TIMEOUT_MESSAGE } from '../../components/common/loadErrorUtils'

export type B2bAction =
  | 'loading this billing cycle'
  | 'saving this room'
  | 'saving these rooms'
  | 'recording the printed tags'
  | 'generating the invoice'
  | 'loading this invoice'
  | 'loading the invoice document'
  | 'recording the payment'
  | 'loading these clients'
  | 'loading this client'
  | 'saving this client'
  | 'saving the rate card'
  | 'loading invoices'

/**
 * Server `code` → copy. The code is the stable contract; the message beside it
 * is written for a developer reading a log.
 */
const BY_CODE: Record<string, string> = {
  CYCLE_ALREADY_INVOICED:
    'This billing cycle has already been invoiced. Start a new cycle to add more rooms.',
  CYCLE_NOT_OPEN:
    'This billing cycle is no longer open, so it cannot be changed. Start a new cycle to add more rooms.',
  B2B_CYCLE_EMPTY:
    'No rooms have been billed in this cycle yet, so there is nothing to invoice. Bill at least one room first.',
  B2B_CYCLE_CLOSED_WITHOUT_INVOICE:
    'This cycle was closed without an invoice. Ask an administrator to look at it before billing continues.',
  B2B_INVOICE_NOT_FOUND: 'That invoice no longer exists.',
  B2B_INVOICE_ORPHANED:
    'The client or billing cycle behind this invoice no longer exists, so the document cannot be produced.',
  INVOICE_NOT_AVAILABLE: 'There is no invoice to show for this yet.',
}

/** Status → copy, when there is no code and no usable message. */
const BY_STATUS: Record<number, (action: B2bAction) => string> = {
  403: () => 'You do not have permission to do that. Ask an administrator to grant B2B access.',
  404: (action) => `That is no longer there. Someone may have removed it while you were ${action}.`,
  409: (action) => `Someone else changed this while you were ${action}. Reload and try again.`,
  413: () => 'That is more data than one request can carry. Save it in smaller batches.',
  422: (action) => `Something in that entry was not accepted while ${action}. Check the highlighted fields.`,
  429: () => 'Too many requests in a row. Wait a few seconds and try again.',
}

/**
 * Does this message read like it was written for a person?
 *
 * Everything here is a signature of a message that escaped from a layer that
 * was not talking to the operator. When in doubt the message is DROPPED - a
 * slightly generic sentence is recoverable, an SQL fragment on a counter screen
 * is not.
 */
const TECHNICAL = [
  /\bundefined\b/i,
  /\bnull\b/i,
  /\bNaN\b/,
  /\n\s*at\s/,                                  // a stack frame
  /\b(?:select|insert into|update|delete from)\b/i,
  /violates|constraint|duplicate key|syntax error|relation ".*" does not exist/i,
  /\b(?:ECONNREFUSED|ETIMEDOUT|ENOTFOUND|ECONNRESET|EPIPE)\b/,
  /\b\d{5}\b/,                                  // a pg SQLSTATE, e.g. 23505
  /\(\s*[45]\d{2}\s*\)/,                        // "Request failed (409)."
  /\b(?:status|code)\s*[:=]?\s*[45]\d{2}\b/i,
  /\[object \w+\]/,
  /^Error:/,
  /\bstack\b/i,
  /<\/?[a-z][^>]*>/i,                           // markup
]

const looksTechnical = (message: string) => TECHNICAL.some((pattern) => pattern.test(message))

/** A sentence, not a fragment: long enough to mean something, short enough to read. */
const readable = (message: string) =>
  message.trim().length >= 8 && message.trim().length <= 300 && !looksTechnical(message)

interface ServerError {
  response?: { status?: number; data?: { message?: string; error?: string; code?: string } }
  code?: string
  message?: string
}

/**
 * The shared 15-second client-side deadline (components/common/loadErrorUtils).
 *
 * Worth its own sentence: "the server answered too slowly" is a different thing
 * from "the request never left", and telling an operator to check their
 * connection when the connection is fine sends them to fix the wrong problem.
 */
const isClientTimeout = (err: ServerError) => {
  if (err.response) return false
  const message = err.message || ''
  // Matched by MEANING, not by the exact literal: axios says "timeout of
  // 12000ms exceeded", the shared helper says "taking too long to respond",
  // and either way the operator needs the same sentence.
  return (
    message === SERVER_TIMEOUT_MESSAGE
    || err.code === 'ECONNABORTED'
    || /timeout|timed out|taking too long/i.test(message)
  )
}

/**
 * The one place a B2B failure becomes words.
 *
 * @param error   whatever was thrown - an axios error, an Error, anything
 * @param action  what the operator was doing, for the fallback sentence
 */
export const b2bErrorMessage = (error: unknown, action: B2bAction): string => {
  const err = (error || {}) as ServerError
  const status = err.response?.status
  const data = err.response?.data

  // 1. A code we have written copy for.
  const code = data?.code || err.code
  if (code && BY_CODE[code]) return BY_CODE[code]

  // 2. A server sentence, but only if it reads like one.
  const serverMessage = data?.message || data?.error
  if (serverMessage && readable(serverMessage)) return serverMessage

  // 3a. The client-side deadline fired - the server is slow, not unreachable.
  if (isClientTimeout(err)) {
    return `${SERVER_TIMEOUT_MESSAGE.replace(/\.$/, '')}. Nothing was changed while ${action}.`
  }

  // 3b. No response at all - the network, not the server.
  if (!err.response) {
    return `Could not reach Rapidry while ${action}. Check the connection and try again.`
  }

  // 4. Status-shaped fallback.
  if (status && BY_STATUS[status]) return BY_STATUS[status](action)

  // 5. Anything else, including every 5xx: say what failed and what is safe.
  return `Something went wrong while ${action}. Nothing was changed. Please try again.`
}

/** Exported for the audit test, which walks every failure shape. */
export const __testing = { BY_CODE, BY_STATUS, looksTechnical, readable }
